(() => {
  const MAX_BYTES = 20 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = new Set([
    "svg", "ai", "eps", "jpg", "jpeg", "bmp", "png", "heic", "tiff", "tif",
    "ppt", "pptx", "doc", "docx", "pdf", "psd",
  ]);
  const uploadedFiles = new WeakMap();

  const extensionOf = (name) => {
    const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
    return match ? match[1] : "";
  };

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const emailLooksValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

  const uploadArtwork = async (file, slot) => {
    const cached = uploadedFiles.get(file);
    if (cached) return cached;

    const stageResponse = await fetch("/apps/1099-builder/engraving-stage", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        slot,
      }),
    });
    const stage = await parseJson(stageResponse);
    if (!stageResponse.ok || !stage?.ok) {
      throw new Error(stage?.error || "We couldn’t prepare that artwork upload.");
    }

    const uploadBody = new FormData();
    for (const parameter of stage.upload.parameters || []) {
      uploadBody.append(parameter.name, parameter.value);
    }
    uploadBody.append("file", file, file.name);

    const directUpload = await fetch(stage.upload.url, {
      method: stage.upload.method || "POST",
      body: uploadBody,
    });
    if (!directUpload.ok) {
      throw new Error("The artwork file could not be uploaded. Please reselect it and try again.");
    }

    const finalizeResponse = await fetch("/apps/1099-builder/engraving-finalize", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: stage.artwork.token }),
    });
    const finalized = await parseJson(finalizeResponse);
    if (!finalizeResponse.ok || !finalized?.ok || !finalized?.artwork?.url) {
      throw new Error(finalized?.error || "We couldn’t finalize that artwork upload.");
    }

    uploadedFiles.set(file, finalized.artwork);
    return finalized.artwork;
  };

  function init(root) {
    if (root.dataset.engravingInitialized === "1") return;
    root.dataset.engravingInitialized = "1";

    const sectionId = root.dataset.sectionId || "";
    const section = root.closest(".shopify-section")
      || document.getElementById(`shopify-section-${sectionId}`)
      || root.parentElement;
    const exactForm = document.getElementById(`product-form-${sectionId}`);
    const fallbackForm = Array.from(section?.querySelectorAll('form[action*="/cart/add"]') || [])
      .find((candidate) => candidate.querySelector('[name="add"]'));
    const form = exactForm || fallbackForm || null;
    const errorEl = root.querySelector("[data-engraving-error]");
    const statusEl = root.querySelector("[data-engraving-status]");
    const nameInput = root.querySelector("[data-proof-name]");
    const emailInput = root.querySelector("[data-proof-email]");
    const phoneInput = root.querySelector("[data-proof-phone]");
    const textInput = root.querySelector("[data-engraving-text]");
    const notesInput = root.querySelector("[data-engraving-notes]");
    const fileInputs = Array.from(root.querySelectorAll("[data-engraving-file]"));
    const slots = Array.from(root.querySelectorAll("[data-artwork-slot]"));
    const dialog = root.querySelector("[data-artwork-requirements-dialog]");
    const variantsNode = root.querySelector("[data-engraving-variants]");
    let variants = [];
    let activeSlotCount = 1;
    let isSubmitting = false;

    try {
      variants = JSON.parse(variantsNode?.textContent || "[]");
    } catch {
      variants = [];
    }

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
    };

    const setStatus = (message) => {
      if (statusEl) statusEl.textContent = message || "";
    };

    if (!form) {
      showError("This customization block could not connect to the product form. Please refresh the page or contact 1099 Designs.");
      return;
    }

    const dynamicCheckout = section?.querySelector(".shopify-payment-button");
    if (dynamicCheckout) dynamicCheckout.hidden = true;

    const primaryButton = () => form.querySelector('[name="add"]')
      || form.querySelector('button[type="submit"], input[type="submit"]');

    const setPrimaryButtonLabel = (label) => {
      const button = primaryButton();
      if (!button || !label) return;
      if (button.tagName === "INPUT") {
        if (button.value !== label) button.value = label;
        return;
      }
      const textNode = button.querySelector("span") || button;
      if (String(textNode.textContent || "").trim() !== label) textNode.textContent = label;
    };

    const setPrimaryButtonDisabled = (disabled) => {
      const button = primaryButton();
      if (button) button.disabled = disabled;
    };

    const selectedVariantId = () => String(form.querySelector('[name="id"]')?.value || "").trim();

    const selectedVariantTitle = () => {
      const id = selectedVariantId();
      return variants.find((variant) => String(variant.id) === id)?.title || "";
    };

    const selectedQuantity = () => {
      const quantityInput = form.querySelector('[name="quantity"]') || section?.querySelector('[name="quantity"]');
      const parsed = Number.parseInt(String(quantityInput?.value || "1"), 10);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
    };

    const updateArtworkSlots = () => {
      const multiDesign = /up to 4 different designs/i.test(selectedVariantTitle());
      activeSlotCount = multiDesign ? 4 : 1;

      slots.forEach((slotEl, index) => {
        const slotNumber = index + 1;
        const visible = slotNumber <= activeSlotCount;
        slotEl.hidden = !visible;
        const label = slotEl.querySelector("[data-artwork-slot-label]");
        if (label) label.textContent = multiDesign ? `Artwork ${slotNumber}` : "Artwork";
        if (!visible) {
          const input = slotEl.querySelector("[data-engraving-file]");
          if (input) input.value = "";
        }
      });
    };

    const validateFile = (file) => {
      if (!file) return "";
      if (file.size > MAX_BYTES) return `${file.name} is larger than 20 MB.`;
      if (!ALLOWED_EXTENSIONS.has(extensionOf(file.name))) {
        return `${file.name} is not an accepted artwork file type.`;
      }
      return "";
    };

    fileInputs.forEach((input) => {
      input.addEventListener("change", () => {
        showError("");
        const file = input.files?.[0];
        const help = input.closest("[data-artwork-slot]")?.querySelector("[data-file-help]");
        if (!file) {
          if (help) help.textContent = "Accepted artwork files · 20 MB max";
          return;
        }

        const problem = validateFile(file);
        if (problem) {
          input.value = "";
          showError(problem);
          if (help) help.textContent = "Please choose another file.";
          return;
        }
        if (help) help.textContent = `Selected: ${file.name}`;
      });
    });

    root.querySelector("[data-artwork-requirements-open]")?.addEventListener("click", () => {
      if (!dialog) return;
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    });

    root.querySelector("[data-artwork-requirements-close]")?.addEventListener("click", () => {
      if (!dialog) return;
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    });

    dialog?.addEventListener("click", (event) => {
      if (event.target === dialog && typeof dialog.close === "function") dialog.close();
    });

    const syncPrimaryButton = () => {
      const button = primaryButton();
      if (!button) return;

      if (root.dataset.engravingSubmitted === "1") {
        if (!button.disabled) button.disabled = true;
        setPrimaryButtonLabel("Submitted for Proof");
        return;
      }

      if (isSubmitting) {
        if (!button.disabled) button.disabled = true;
        setPrimaryButtonLabel("Submitting for Proof…");
        return;
      }

      if (!button.disabled) setPrimaryButtonLabel("Submit for Proof");
    };

    const refreshEngravingUi = () => {
      updateArtworkSlots();
      const checkout = section?.querySelector(".shopify-payment-button");
      if (checkout) checkout.hidden = true;
      syncPrimaryButton();
    };

    const resultParams = new URLSearchParams(window.location.search);
    const proofState = resultParams.get("engraving_proof");
    if (proofState === "submitted") {
      const proofRef = String(resultParams.get("proof_ref") || "").trim();
      root.dataset.engravingSubmitted = "1";
      setStatus(`Proof request${proofRef ? ` ${proofRef}` : ""} received. We’ll review your engraving details and send your digital proof before invoicing.`);
    } else if (proofState === "error") {
      showError(resultParams.get("proof_error") || "We couldn’t submit your engraving proof request. Please try again.");
    }

    if (proofState === "submitted" || proofState === "error") {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("engraving_proof");
      cleanUrl.searchParams.delete("proof_ref");
      cleanUrl.searchParams.delete("proof_error");
      window.history.replaceState({}, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }

    form.addEventListener("change", () => window.setTimeout(refreshEngravingUi, 0));
    refreshEngravingUi();

    const buttonObserver = new MutationObserver(() => syncPrimaryButton());
    if (section) {
      buttonObserver.observe(section, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["disabled"],
      });
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (isSubmitting || root.dataset.engravingSubmitted === "1") return;

      refreshEngravingUi();
      showError("");
      setStatus("");

      const customerName = String(nameInput?.value || "").trim();
      const customerEmail = String(emailInput?.value || "").trim();
      const customerPhone = String(phoneInput?.value || "").trim();
      const engravingText = String(textInput?.value || "").trim();
      const notes = String(notesInput?.value || "").trim();
      const variantId = selectedVariantId();
      const activeInputs = fileInputs.slice(0, activeSlotCount);
      const selectedFiles = activeInputs
        .map((input, index) => ({ file: input.files?.[0] || null, slot: index + 1 }))
        .filter((item) => item.file);

      if (!customerName) {
        showError("Please enter your name so we know who the proof is for.");
        nameInput?.focus();
        root.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (!emailLooksValid(customerEmail)) {
        showError("Please enter a valid email address so we can send your proof.");
        emailInput?.focus();
        root.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (!variantId) {
        showError("Please select a product option before submitting for proof.");
        root.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (!engravingText && selectedFiles.length === 0) {
        showError("Please enter engraving text, upload artwork, or use both.");
        root.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      for (const item of selectedFiles) {
        const problem = validateFile(item.file);
        if (problem) {
          showError(problem);
          root.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }

      isSubmitting = true;
      setPrimaryButtonDisabled(true);
      setPrimaryButtonLabel("Submitting for Proof…");

      try {
        let uploaded = [];
        if (selectedFiles.length) {
          setStatus(selectedFiles.length === 1 ? "Uploading artwork…" : `Uploading ${selectedFiles.length} artwork files…`);
          uploaded = await Promise.all(
            selectedFiles.map((item) => uploadArtwork(item.file, item.slot)),
          );
        }

        setStatus("Submitting your proof request…");

        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete("engraving_proof");
        returnUrl.searchParams.delete("proof_ref");
        returnUrl.searchParams.delete("proof_error");
        const returnPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;

        const proofForm = document.createElement("form");
        proofForm.method = "POST";
        proofForm.action = "/apps/1099-builder/engraving-submit";
        proofForm.hidden = true;

        const addField = (name, value) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = String(value ?? "");
          proofForm.appendChild(input);
        };

        addField("return_path", returnPath);
        addField("customer_name", customerName);
        addField("customer_email", customerEmail);
        addField("customer_phone", customerPhone);
        addField("variant_id", variantId);
        addField("quantity", selectedQuantity());
        addField("engraving_text", engravingText);
        addField("engraving_notes", notes);
        addField("artworks", JSON.stringify(uploaded.map((artwork) => ({
          id: artwork.id,
          url: artwork.url,
          filename: artwork.filename,
          slot: artwork.slot,
        }))));

        document.body.appendChild(proofForm);
        proofForm.submit();
      } catch (error) {
        isSubmitting = false;
        setPrimaryButtonDisabled(false);
        setPrimaryButtonLabel("Submit for Proof");
        setStatus("");
        showError(error instanceof Error ? error.message : "We couldn’t submit your engraving proof request. Please try again.");
        root.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, true);
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll?.("[data-engraving-personalization]").forEach(init);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
